/**
 * BrandingSection — miolo de edição de Identidade Visual (Brief 5 A3), extraído
 * de `pages/BrandingPage.tsx` (Admin Hub T7 — Correction 1 do dispatch).
 *
 * O corpo é o MESMO de `BrandingPageInner` — logo, cores, fonte secundária,
 * favicon, assinatura de email e (Regulated) headers/footers de conformidade —
 * menos duas coisas que agora vêm de quem compõe esta seção:
 *   - o `<h1>` de página ("Branding e Identidade Visual") — dentro de uma
 *     `DomainSection` o título já vem do catálogo (item.label); dentro de
 *     `pages/BrandingPage.tsx` (rota antiga, só viva pelo próprio spec — a
 *     rota `/admin/branding` já redireciona por T3) o header próprio compõe
 *     esta seção por fora.
 *   - o botão local "Salvar branding" — a seção registra na `StickySaveBar`
 *     do domínio via `useSectionSave`, como `GeneralTab` (mesmo padrão T7).
 *
 * O gate de LEITURA (`tenant-config-read`) NÃO mora aqui — quem compõe decide
 * (o `DomainSection` do hub, ou o `BrandingPage()` da rota antiga): dois
 * decisores de gate para a mesma seção seria a divergência que a T7 já
 * corrigiu no `DomainSection` (CapabilityLockedBanner). O gate de ESCRITA
 * (`tenant-config-manage` + tier >= Team) é próprio da seção (Solo lê mas não
 * escreve — 403 no service) e continua aqui, como sempre foi.
 *
 * `.brand-scope` é auto-contido: esta seção importa o próprio `branding.css` e
 * se embrulha na classe, então funciona igual em qualquer lugar que a compose
 * (não depende do pai fornecer o scope). `.brand-page` (altura 100% + padding
 * de página cheia) NÃO é usado aqui — dentro de uma `DomainSection` isso
 * colidiria com o padding que o hub já dá; só a rota antiga precisa de layout
 * de página cheia, e resolve isso no PRÓPRIO `pages/BrandingPage.tsx`.
 */

import { CapabilityLockedBanner } from '@lume/ui-components';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import {
  useDeleteBrandingAsset,
  useTenantBranding,
  useUpdateTenantBranding,
  useUploadBrandingAsset,
} from '../../../hooks/use-tenant-branding';
import { useTenantTier } from '../../../hooks/use-tenant-tier';
import { useSectionSave } from '../hub/save-bar-context';

import { AssetUploader } from './AssetUploader';
import {
  brandingFormFromSerialized,
  buildBrandingPatch,
  isPatchEmpty,
  validateBrandingForm,
  type BrandingFormState,
} from './branding-form';
import { BrandingPreviewPanel } from './BrandingPreviewPanel';
import {
  BRANDING_FIELD_LABELS,
  CERT_CATALOG,
  FONT_OPTIONS,
  MAX_CERTIFICATIONS,
  MAX_TEXT_LEN,
  PRIMARY_SWATCHES,
  SECONDARY_SWATCHES,
  isRegulated,
  isTeamPlus,
  type BrandingCertification,
  type SecondaryFont,
  type SerializedTenantBranding,
  type UpdateTenantBrandingRequest,
} from './types';

import './branding.css';

const EMPTY_FORM: BrandingFormState = {
  primaryColor: '',
  secondaryColor: '',
  secondaryFont: '',
  emailSignature: '',
  regulatedHeaderText: '',
  certifications: [],
};

/** Labels dos campos presentes em `patch`, na ordem canônica de `BRANDING_FIELD_LABELS`. */
function changedLabelsOf(patch: UpdateTenantBrandingRequest): string[] {
  return (Object.keys(patch) as (keyof UpdateTenantBrandingRequest)[]).map(
    (k) => BRANDING_FIELD_LABELS[k],
  );
}

/** Patch inverso: para cada campo em `patch`, o valor ANTIGO (de `before`) — usado no `undo`. */
function buildUndoPatch(
  patch: UpdateTenantBrandingRequest,
  before: SerializedTenantBranding,
): UpdateTenantBrandingRequest {
  const undo: UpdateTenantBrandingRequest = {};
  if ('primaryColor' in patch) undo.primaryColor = before.primaryColor;
  if ('secondaryColor' in patch) undo.secondaryColor = before.secondaryColor;
  if ('secondaryFont' in patch) undo.secondaryFont = before.secondaryFont as SecondaryFont | null;
  if ('emailSignature' in patch) undo.emailSignature = before.emailSignature;
  if ('regulatedHeaderText' in patch) undo.regulatedHeaderText = before.regulatedHeaderText;
  if ('certifications' in patch) undo.certifications = before.certifications;
  return undo;
}

export function BrandingSection(): JSX.Element {
  const tier = useTenantTier();
  const canManageCap = useCapability('tenant-config-manage');
  const teamPlus = isTeamPlus(tier);
  const regulated = isRegulated(tier);
  // Escrita real só com a cap E tier >= Team (Solo recebe 403 no service).
  const canManage = canManageCap && teamPlus;

  // Leitura habilitada — quem compõe esta seção já decidiu que há acesso.
  const { data: branding, isLoading, isError } = useTenantBranding(true);
  const update = useUpdateTenantBranding();
  const uploadAsset = useUploadBrandingAsset();
  const deleteAsset = useDeleteBrandingAsset();

  const [form, setForm] = useState<BrandingFormState>(EMPTY_FORM);
  const [assetError, setAssetError] = useState<string | null>(null);

  // Hidrata o form quando o serializer chega/atualiza.
  useEffect(() => {
    if (branding !== undefined) setForm(brandingFormFromSerialized(branding));
  }, [branding]);

  const validationError = validateBrandingForm(form);
  const patch = useMemo(
    () => (branding !== undefined ? buildBrandingPatch(form, branding) : {}),
    [form, branding],
  );
  const dirty = !isPatchEmpty(patch);
  const changedLabels = useMemo(() => changedLabelsOf(patch), [patch]);
  const assetBusy = uploadAsset.isPending || deleteAsset.isPending;

  const previousValues = useRef<UpdateTenantBrandingRequest | null>(null);

  // Registro na StickySaveBar do domínio (Admin Hub T7 — mesmo padrão de
  // GeneralTab). `useSectionSave` precisa ser chamado incondicionalmente em
  // todo render — a IIFE decide `null` vs a registration, e fecha sobre
  // `currentBranding` (const, narrowed) para os callbacks não verem
  // `branding` como possivelmente `undefined`.
  useSectionSave(
    (() => {
      if (!dirty || !canManage || validationError !== null || branding === undefined) return null;
      const currentBranding = branding;
      return {
        sectionId: 'identidade-visual',
        changedLabels,
        save: async () => {
          previousValues.current = buildUndoPatch(patch, currentBranding);
          await update.mutateAsync(patch);
        },
        discard: () => setForm(brandingFormFromSerialized(currentBranding)),
        undo: async () => {
          if (previousValues.current !== null) {
            await update.mutateAsync(previousValues.current);
          }
        },
      };
    })(),
  );

  function patchForm(p: Partial<BrandingFormState>): void {
    setForm((prev) => ({ ...prev, ...p }));
  }

  function toggleCert(entry: BrandingCertification): void {
    if (!canManage) return;
    const exists = form.certifications.some((c) => c.id === entry.id);
    if (exists) {
      patchForm({ certifications: form.certifications.filter((c) => c.id !== entry.id) });
    } else if (form.certifications.length < MAX_CERTIFICATIONS) {
      patchForm({ certifications: [...form.certifications, { id: entry.id, label: entry.label }] });
    }
  }

  function handleUpload(kind: 'logo' | 'favicon', file: File): void {
    setAssetError(null);
    uploadAsset.mutate(
      { kind, file },
      {
        onError: (err) =>
          setAssetError(err.message !== '' ? err.message : 'Falha ao enviar o arquivo.'),
      },
    );
  }

  function handleRemove(kind: 'logo' | 'favicon'): void {
    setAssetError(null);
    deleteAsset.mutate(kind, {
      onError: (err) =>
        setAssetError(err.message !== '' ? err.message : 'Falha ao remover o arquivo.'),
    });
  }

  if (isLoading) {
    return (
      <div className="brand-scope">
        <p className="brand-foot-hint">Carregando branding…</p>
      </div>
    );
  }
  if (isError || branding === undefined) {
    return (
      <div className="brand-scope">
        <p className="brand-alert brand-alert--error" role="alert">
          Falha ao carregar o branding do tenant.
        </p>
      </div>
    );
  }

  return (
    <div className="brand-scope">
      <header className="brand-pagehead">
        <p className="brand-pagehead__sub">
          Customização visual leve do tenant. A identidade Lume permanece dominante para preservar a
          consistência da experiência. Ajustes afetam o logo no header, accent de botões, assinatura
          de emails e (em Regulated) headers/footers de conformidade.
        </p>
      </header>

      {!teamPlus && (
        <CapabilityLockedBanner
          capability="tenant-config-manage"
          tierMin="team"
          variant="block"
          fallbackMessage="Tenant Solo herda branding mínimo na configuração Geral (A2). A customização completa (cores, fonte, logo, favicon) requer tier Team+. Esta tela está em modo leitura."
        />
      )}

      {regulated && (
        <div className="brand-banner brand-banner--warn">
          <div>
            <strong>Tier Regulated · headers/footers de conformidade.</strong> Mudanças em branding
            regulado exigem aprovação dupla (Admin + DPO) e revisão periódica a cada 6 meses —{' '}
            <em>fluxo de aprovação em breve</em>. Logos de certificação devem corresponder a
            credenciais ativas.
          </div>
        </div>
      )}

      {assetError !== null && (
        <p className="brand-alert brand-alert--error" role="alert">
          {assetError}
        </p>
      )}

      <div className="brand-grid">
        <div className="brand-col">
          {/* Logo */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Logo do tenant</span>
              <span className="brand-card__caption">PNG · SVG</span>
            </div>
            <span className="brand-card__hint">
              Formato transparente recomendado. Aparece no header do painel. Máx 200 KB.
            </span>
            <AssetUploader
              kind="logo"
              url={branding.logoUrl}
              canManage={canManage}
              isBusy={assetBusy}
              onUpload={(file) => handleUpload('logo', file)}
              onRemove={() => handleRemove('logo')}
            />
          </section>

          {/* Cor primária */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Cor primária</span>
              <span className="brand-card__caption">swatches curados</span>
            </div>
            <span className="brand-card__hint">
              Aplicada a accents (botões, badges, links). Soma à paleta Lume, não a substitui.
            </span>
            <div className="brand-swatches">
              {PRIMARY_SWATCHES.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  className={`brand-swatch${form.primaryColor === c.hex ? ' brand-swatch--active' : ''}`}
                  disabled={!canManage}
                  onClick={() => patchForm({ primaryColor: c.hex })}
                >
                  <span className="brand-swatch__chip" style={{ background: c.hex }} />
                  <span className="brand-swatch__txt">
                    <span className="brand-swatch__name">{c.name}</span>
                    <span className="brand-swatch__hex">
                      {c.hex} · {c.hint}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Cor secundária (Team+) */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Cor secundária</span>
              <span className="brand-card__caption">Team+ · opcional</span>
            </div>
            <span className="brand-card__hint">
              Usada em dividers, sub-texto decorativo e accents discretos.
            </span>
            <div className="brand-swatches">
              {SECONDARY_SWATCHES.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  className={`brand-swatch${form.secondaryColor === c.hex ? ' brand-swatch--active' : ''}`}
                  disabled={!canManage}
                  onClick={() => patchForm({ secondaryColor: c.hex })}
                >
                  <span className="brand-swatch__chip" style={{ background: c.hex }} />
                  <span className="brand-swatch__txt">
                    <span className="brand-swatch__name">{c.name}</span>
                    <span className="brand-swatch__hex">{c.hex}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Fonte secundária (Team+) */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Fonte secundária</span>
              <span className="brand-card__caption">Team+</span>
            </div>
            <span className="brand-card__hint">
              Aplicada em headlines e títulos editoriais. A fonte primária (Geist) permanece na UI
              core.
            </span>
            <div className="brand-fonts">
              {FONT_OPTIONS.map((f) => (
                <button
                  type="button"
                  key={f.id}
                  className={`brand-font${form.secondaryFont === f.id ? ' brand-font--active' : ''}`}
                  disabled={!canManage}
                  onClick={() => patchForm({ secondaryFont: f.id satisfies SecondaryFont })}
                >
                  <span className="brand-font__sample" style={{ fontFamily: f.family }}>
                    Excelência comunicacional.
                  </span>
                  <span className="brand-font__meta">
                    <span className="brand-font__name">{f.id}</span>
                    <span className="brand-font__hint">{f.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Favicon (Team+) */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Favicon</span>
              <span className="brand-card__caption">Team+ · PNG/ICO</span>
            </div>
            <span className="brand-card__hint">
              Ícone exibido nas abas do navegador quando colaboradores acessam o painel.
            </span>
            <AssetUploader
              kind="favicon"
              url={branding.faviconUrl}
              canManage={canManage}
              isBusy={assetBusy}
              onUpload={(file) => handleUpload('favicon', file)}
              onRemove={() => handleRemove('favicon')}
            />
          </section>

          {/* Assinatura de email */}
          <section className="brand-card">
            <div className="brand-card__head">
              <span className="brand-card__title">Assinatura de email</span>
              <span className="brand-card__caption">bloco final dos emails do tenant</span>
            </div>
            <div className="brand-field">
              <textarea
                className="brand-textarea"
                rows={5}
                maxLength={MAX_TEXT_LEN}
                value={form.emailSignature}
                disabled={!canManage}
                onChange={(e) => patchForm({ emailSignature: e.target.value })}
                placeholder={'Equipe NetComet\n+55 51 3XXX-XXXX\natendimento@netcomet.com.br'}
              />
              <span className="brand-counter">
                {String(form.emailSignature.length)}/{String(MAX_TEXT_LEN)}
              </span>
            </div>
          </section>

          {/* Regulated — headers/footers conformidade */}
          {regulated && (
            <section className="brand-card brand-card--accent">
              <div className="brand-card__head">
                <span className="brand-card__title">Headers e footers de conformidade</span>
                <span className="brand-card__caption">Regulated</span>
              </div>
              <span className="brand-card__hint">
                Texto obrigatório no topo de toda página + logos de certificação no rodapé.
              </span>
              <div className="brand-field">
                <span className="brand-field__label">Texto do header</span>
                <input
                  className="brand-input"
                  type="text"
                  maxLength={MAX_TEXT_LEN}
                  value={form.regulatedHeaderText}
                  disabled={!canManage}
                  onChange={(e) => patchForm({ regulatedHeaderText: e.target.value })}
                  placeholder="Sistema regulado pela ANS · Portaria XYZ/2024"
                />
              </div>
              <div className="brand-field">
                <span className="brand-field__label">
                  Logos de certificação (até {String(MAX_CERTIFICATIONS)})
                </span>
                <div className="brand-certs">
                  {CERT_CATALOG.map((cert) => {
                    const active = form.certifications.some((c) => c.id === cert.id);
                    return (
                      <button
                        type="button"
                        key={cert.id}
                        className={`brand-cert${active ? ' brand-cert--active' : ''}`}
                        disabled={
                          !canManage ||
                          (!active && form.certifications.length >= MAX_CERTIFICATIONS)
                        }
                        onClick={() => toggleCert({ id: cert.id, label: cert.label })}
                      >
                        <span className="brand-cert__chip">{cert.icon}</span>
                        <span className="brand-cert__grow">{cert.label}</span>
                        {active && <span className="brand-cert__check">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>

        <BrandingPreviewPanel
          primaryColor={form.primaryColor}
          secondaryColor={form.secondaryColor}
          secondaryFont={form.secondaryFont}
          logoUrl={branding.logoUrl}
          emailSignature={form.emailSignature}
          regulatedHeaderText={form.regulatedHeaderText}
          certifications={form.certifications}
          showRegulated={regulated}
        />
      </div>
    </div>
  );
}
